/**
 * TransitionsLog — the Änderungen sub-feed of the Verlauf panel (#1913
 * C). Two affordances over the append-only ``twin_attribute_transitions``
 * substrate:
 *
 *  1. A chronological log (newest-first) of attribute transitions, each
 *     row carrying its document provenance as a click-through (the
 *     existing ``onPreviewDocument`` thread) where the recorder had one.
 *  2. A per-attribute value-history toggle: clicking an attribute name
 *     opens its value series below the log — a numeric attribute as a
 *     small chart, a categorical one as a dated value list
 *     (``AttributeHistoryView``).
 */
import React, { useMemo, useState } from 'react';
import { FileText } from 'lucide-react';
import type { AttributeTransition, EntitySourceDoc, LenseField } from '../../../services/twinService';
import { formatDateString } from '../formatValue';
import { attributeLabel, describeTransition, provenanceLabel, type TransitionDescription } from './attributeHistory';
import AttributeHistoryView from './AttributeHistoryView';
import { mergeFieldValueLabels } from './editValue';

export interface TransitionsLogProps {
  transitions: AttributeTransition[];
  entityType: string;
  entityId: string;
  fields?: LenseField[];
  /**
   * The card's value-label projection (`attribute → raw → label`, HOF-696 D20), so the log
   * says "gesperrt → freigegeben" where the card says "freigegeben". Absent: the fields'
   * own lens `value_labels`.
   */
  valueLabels?: Record<string, Record<string, string>>;
  /** Linked source docs by id — resolves the "Beleg <year>" provenance. */
  docsById?: Map<string, EntitySourceDoc>;
  onPreviewDocument?: (docId: string) => void;
}

/** The compact "old → new" caption, framed by the transition mode. */
function changeCaption(desc: TransitionDescription): string {
  switch (desc.mode) {
    case 'set':
      return `→ ${desc.newText}`;
    case 'removed':
      return `${desc.oldText} → entfernt`;
    default:
      return `${desc.oldText} → ${desc.newText}`;
  }
}

export const TransitionsLog: React.FC<TransitionsLogProps> = ({
  transitions,
  entityType,
  entityId,
  fields,
  valueLabels,
  docsById,
  onPreviewDocument,
}) => {
  const [selected, setSelected] = useState<string | null>(null);
  const labels = useMemo(() => valueLabels ?? mergeFieldValueLabels(fields, undefined), [valueLabels, fields]);

  const fieldById = useMemo(() => {
    const map = new Map<string, LenseField>();
    for (const f of fields ?? []) map.set(f.id, f);
    return map;
  }, [fields]);

  // Defensive newest-first ordering (the API already returns this order).
  // Derived fields (ontology ``kind: derived`` — system-computed from other
  // attributes, e.g. the IBAN fragments off ``iban``) are not independent
  // facts: their transitions are echoes of the source field's and stay out
  // of the log. Schema-driven — no field ids hardcoded here.
  const ordered = useMemo(
    () =>
      transitions
        .filter((t) => fieldById.get(t.attribute)?.kind !== 'derived')
        .sort((a, b) => b.valid_from.localeCompare(a.valid_from)),
    [transitions, fieldById],
  );

  if (ordered.length === 0) return null;

  const selectedField = selected ? fieldById.get(selected) : undefined;

  return (
    <div className="transitions-log">
      <h4 className="transitions-log__heading">Änderungen</h4>
      <ul className="transitions-log__list">
        {ordered.map((t) => {
          const desc = describeTransition(t, fieldById.get(t.attribute), labels?.[t.attribute]);
          const isOpen = selected === t.attribute;
          // A Beleg chip renders ONLY when the source resolves to a real linked
          // document — a bulk-backfill marker resolves to none, so no chip (#2076).
          const beleg = provenanceLabel(t.source_id, docsById);
          return (
            <li key={t.id} className="transitions-log__row">
              <span className="transitions-log__date">{formatDateString(t.valid_from)}</span>
              <span className="transitions-log__body">
                <span className="transitions-log__line">
                  <button
                    type="button"
                    className={`transitions-log__attr${isOpen ? ' transitions-log__attr--open' : ''}`}
                    aria-pressed={isOpen}
                    onClick={() => setSelected(isOpen ? null : t.attribute)}
                  >
                    {desc.label}
                  </button>
                  <span className="transitions-log__change">{changeCaption(desc)}</span>
                </span>
                {beleg && t.source_id && onPreviewDocument && (
                  <button
                    type="button"
                    className="transitions-log__beleg"
                    onClick={() => onPreviewDocument(t.source_id!)}
                  >
                    <FileText size={12} />
                    {beleg}
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {selected && (
        <div className="transitions-log__history">
          <div className="transitions-log__history-head">
            <span className="transitions-log__history-title">
              Werteverlauf: {attributeLabel(selected, selectedField)}
            </span>
            <button type="button" className="transitions-log__history-close" onClick={() => setSelected(null)}>
              Schließen
            </button>
          </div>
          <AttributeHistoryView
            entityType={entityType}
            entityId={entityId}
            attribute={selected}
            field={selectedField}
            valueLabels={labels?.[selected]}
            docsById={docsById}
            onPreviewDocument={onPreviewDocument}
          />
        </div>
      )}
    </div>
  );
};

export default TransitionsLog;
